import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Eye, Edit2, Trash2, Users, X } from 'lucide-react';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { CountryCombobox } from '@/components/CountryCombobox';
import { CountryFilter } from '@/components/CountryFilter';
import { DEFAULT_COUNTRY } from '@/lib/countries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  first_name: '', last_name: '', email: '', phone: '', club_id: '',
  roles: [],
};

const roleTypeLabel = { organisation: 'Organisation', venue: 'Venue' };

export default function KeyContactsTab({ currentUser, clubs, venues, embedded = false, country: externalCountry }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [duplicateWarn, setDuplicateWarn] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [internalCountry, setInternalCountry] = useState('');
  // Parent (People & Places) owns the country filter when embedded.
  const countryFilter = embedded ? (externalCountry || '') : internalCountry;
  const setCountryFilter = setInternalCountry;

  const clubMap = useMemo(() => Object.fromEntries((clubs || []).map(c => [c.club_id, c])), [clubs]);
  const venueMap = useMemo(() => Object.fromEntries((venues || []).map(v => [v.venue_id, v])), [venues]);

  const canMutate = (c) => {
    if (!currentUser) return false;
    if (currentUser.role === 'super') return true;
    return currentUser.club_id && currentUser.club_id === c.created_by_org_id;
  };

  const load = async (country = countryFilter) => {
    setLoading(true);
    try {
      const params = country ? { country } : {};
      const r = await axios.get(`${API}/key-contacts`, { withCredentials: true, params });
      setContacts(r.data || []);
    } catch (e) {
      toast.error('Failed to load key contacts');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!countryFilter) return;
    load(countryFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryFilter]);

  // Duplicate check as user types
  useEffect(() => {
    const fn = form.first_name.trim();
    const ln = form.last_name.trim();
    if (!fn || !ln) { setDuplicateWarn(false); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/key-contacts/duplicate-check`, {
          params: { first_name: fn, last_name: ln, exclude_id: editing?.contact_id || undefined },
          withCredentials: true,
        });
        setDuplicateWarn(!!r.data?.duplicate);
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [form.first_name, form.last_name, editing]);

  const openCreate = async () => {
    setEditing(null);
    // Default the country to the current user's country (falling back
    // to New Zealand) — matches the Feb 2026 rollout rule.
    let country = DEFAULT_COUNTRY;
    try {
      const r = await axios.get(`${API}/countries/my-default`, { withCredentials: true });
      if (r.data?.country) country = r.data.country;
    } catch { /* keep the fallback */ }
    setForm({ ...emptyForm, country });
    setFormOpen(true);
    setDuplicateWarn(false);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      first_name: c.first_name || '', last_name: c.last_name || '',
      email: c.email || '', phone: c.phone || '',
      club_id: c.club_id || '',
      country: c.country || DEFAULT_COUNTRY,
      roles: (c.roles || []).map(r => ({ ...r })),
    });
    setFormOpen(true);
  };
  const openView = (c) => { setViewing(c); setViewOpen(true); };

  const addRole = () => setForm(f => ({
    ...f, roles: [...f.roles, { role_name: '', linked_type: 'organisation', linked_id: '' }],
  }));
  const updateRole = (i, patch) => setForm(f => ({
    ...f, roles: f.roles.map((r, idx) => idx === i ? { ...r, ...patch } : r),
  }));
  const removeRole = (i) => setForm(f => ({ ...f, roles: f.roles.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error('First and last name are required'); return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error('Provide at least one method of contact (email or phone)'); return;
    }
    for (const r of form.roles) {
      if (!r.role_name.trim() || !r.linked_id) {
        toast.error('Each role needs a name and a linked organisation or venue'); return;
      }
    }
    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      club_id: form.club_id || null,
      country: form.country || null,
      roles: form.roles,
    };
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${API}/key-contacts/${editing.contact_id}`, payload, { withCredentials: true });
        toast.success('Key contact updated');
      } else {
        await axios.post(`${API}/key-contacts`, payload, { withCredentials: true });
        toast.success('Key contact created');
      }
      setFormOpen(false);
      await load(countryFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/key-contacts/${deleteTarget.contact_id}`, { withCredentials: true });
      toast.success('Key contact deleted');
      setDeleteOpen(false); setDeleteTarget(null);
      await load(countryFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const orgVenueLabel = (c) => {
    const labels = [];
    if (c.club_id) {
      const club = clubMap[c.club_id];
      if (club) labels.push(club.short_name || club.abbreviation || club.name);
    }
    (c.roles || []).forEach(r => {
      if (r.linked_type === 'organisation') {
        const club = clubMap[r.linked_id];
        if (club) labels.push(club.short_name || club.abbreviation || club.name);
      } else if (r.linked_type === 'venue') {
        const v = venueMap[r.linked_id];
        if (v) labels.push(v.venue_name || v.name);
      }
    });
    const unique = [...new Set(labels.filter(Boolean))];
    return unique.length ? unique.join(', ') : '-';
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold" style={{ color: '#2D4A3E' }}>Key Contacts</h2>
          <p className="text-sm text-muted-foreground">
            People who are contact points for skating bodies and venues.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!embedded && <CountryFilter value={countryFilter} onChange={setCountryFilter} testId="key-contacts-country-filter" />}
          <Button onClick={openCreate} className="btn-primary" data-testid="key-contact-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Contact
          </Button>
        </div>
      </div>
      <Card className="p-6" data-testid="key-contacts-panel">

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-stone-200 text-left text-sm text-muted-foreground">
              <th className="py-3 px-2">Name</th>
              <th className="py-3 px-2">Email</th>
              <th className="py-3 px-2">Organisation/Venue</th>
              <th className="py-3 px-2">Phone</th>
              <th className="py-3 px-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && contacts.length === 0 && (
              <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                No key contacts yet — click &quot;Add Contact&quot; to create the first.
              </td></tr>
            )}
            {contacts.map(c => (
              <tr key={c.contact_id} className="border-b border-stone-100 hover:bg-stone-50"
                  data-testid={`key-contact-row-${c.contact_id}`}>
                <td className="py-3 px-2 font-medium">{c.first_name} {c.last_name}</td>
                <td className="py-3 px-2 text-sm">{c.email || '-'}</td>
                <td className="py-3 px-2 text-sm">{orgVenueLabel(c)}</td>
                <td className="py-3 px-2 text-sm">{c.phone || '-'}</td>
                <td className="py-3 px-2">
                  <div className="flex items-center justify-center gap-2">
                    <button onClick={() => openView(c)} className="action-icon"
                            title="View" data-testid={`view-contact-${c.contact_id}`}>
                      <Eye className="h-4 w-4" />
                    </button>
                    {canMutate(c) && (
                      <>
                        <button onClick={() => openEdit(c)} className="action-icon"
                                title="Edit" data-testid={`edit-contact-${c.contact_id}`}>
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button onClick={() => { setDeleteTarget(c); setDeleteOpen(true); }}
                                className="action-icon text-red-600 hover:text-red-800"
                                title="Delete" data-testid={`delete-contact-${c.contact_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Key Contact' : 'Add Key Contact'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })}
                       required data-testid="kc-firstname-input" />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })}
                       required data-testid="kc-lastname-input" />
              </div>
            </div>

            {duplicateWarn && (
              <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-md px-3 py-2"
                   data-testid="kc-duplicate-warn">
                A key contact named &quot;{form.first_name} {form.last_name}&quot; already exists. You can still save if this is a different person, otherwise please edit the existing record.
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                       data-testid="kc-email-input" />
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                       placeholder="e.g., 021 123 4567" data-testid="kc-phone-input" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">At least one of email or phone is required.</p>

            <div>
              <Label>Club Membership</Label>
              <select className="w-full border rounded-lg px-3 py-2 bg-white"
                      value={form.club_id} onChange={e => setForm({ ...form, club_id: e.target.value })}
                      data-testid="kc-club-select">
                <option value="">-- Select --</option>
                {(clubs || []).map(c => (
                  <option key={c.club_id} value={c.club_id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Country</Label>
              <div className="mt-1">
                <CountryCombobox
                  value={form.country}
                  onChange={(v) => setForm({ ...form, country: v })}
                  testId="kc-country"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Roles</Label>
                <Button type="button" size="sm" variant="outline" onClick={addRole}
                        data-testid="kc-add-role-btn">
                  <Plus className="h-3 w-3 mr-1" /> Add Role
                </Button>
              </div>
              {form.roles.length === 0 && (
                <p className="text-xs text-muted-foreground">No roles yet — click &quot;Add Role&quot; to add one.</p>
              )}
              <div className="space-y-3">
                {form.roles.map((r, i) => (
                  <div key={i} className="border rounded-md p-3 bg-stone-50" data-testid={`kc-role-row-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Input placeholder="Role name (e.g., President, Rink Manager)"
                             value={r.role_name}
                             onChange={e => updateRole(i, { role_name: e.target.value })}
                             className="flex-1" data-testid={`kc-role-name-${i}`} />
                      <button type="button" onClick={() => removeRole(i)}
                              className="action-icon text-red-600 hover:text-red-800"
                              title="Remove role" data-testid={`kc-role-remove-${i}`}>
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-4 mb-2 text-sm">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name={`role-type-${i}`} value="organisation"
                               checked={r.linked_type === 'organisation'}
                               onChange={() => updateRole(i, { linked_type: 'organisation', linked_id: '' })}
                               data-testid={`kc-role-type-org-${i}`} />
                        Organisation
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name={`role-type-${i}`} value="venue"
                               checked={r.linked_type === 'venue'}
                               onChange={() => updateRole(i, { linked_type: 'venue', linked_id: '' })}
                               data-testid={`kc-role-type-venue-${i}`} />
                        Venue
                      </label>
                    </div>
                    <select className="w-full border rounded-lg px-3 py-2 bg-white text-sm"
                            value={r.linked_id}
                            onChange={e => updateRole(i, { linked_id: e.target.value })}
                            data-testid={`kc-role-linked-${i}`}>
                      <option value="">-- Select {roleTypeLabel[r.linked_type]} --</option>
                      {r.linked_type === 'organisation'
                        ? (clubs || []).map(c => <option key={c.club_id} value={c.club_id}>{c.name}</option>)
                        : (venues || []).map(v => <option key={v.venue_id} value={v.venue_id}>{v.venue_name || v.name}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}
                      className="flex-1" data-testid="kc-cancel-btn">Cancel</Button>
              <Button type="submit" className="flex-1 btn-primary" disabled={saving}
                      data-testid="kc-save-btn">
                {saving ? 'Saving…' : (editing ? 'Update Contact' : 'Create Contact')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View (read-only) Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-xl" data-testid="kc-view-dialog">
          <DialogHeader>
            <DialogTitle>{viewing?.first_name} {viewing?.last_name}</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><div className="text-xs text-muted-foreground">Email</div><div>{viewing.email || '-'}</div></div>
                <div><div className="text-xs text-muted-foreground">Phone</div><div>{viewing.phone || '-'}</div></div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Club Membership</div>
                <div>{viewing.club_id ? (clubMap[viewing.club_id]?.name || viewing.club_id) : '-'}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Country</div>
                <div>{viewing.country || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Roles</div>
                {(viewing.roles || []).length === 0 && <div>-</div>}
                <ul className="space-y-1">
                  {(viewing.roles || []).map((r, i) => {
                    const target = r.linked_type === 'organisation'
                      ? clubMap[r.linked_id]?.name
                      : (venueMap[r.linked_id]?.venue_name || venueMap[r.linked_id]?.name);
                    return (
                      <li key={i}>
                        <span className="font-medium">{r.role_name}</span>
                        {' — '}
                        <span className="text-muted-foreground">{roleTypeLabel[r.linked_type]}:</span>{' '}
                        {target || r.linked_id}
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="border-t pt-3 mt-3">
                <div className="text-xs text-muted-foreground">Record Owner</div>
                <div>
                  {viewing.created_by_name}
                  {viewing.created_by_org_short && (
                    <span className="text-muted-foreground"> ({viewing.created_by_org_short})</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        itemName={deleteTarget ? `${deleteTarget.first_name} ${deleteTarget.last_name}` : ''}
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </Card>
    </>
  );
}
